import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ButtonModule } from 'primeng/button';
import { TooltipModule } from 'primeng/tooltip';
import { ApprovalWorkflowComponent, FLOW_TYPE } from '../../../../../@shared/components/approval-workflow/approval-workflow.component';
import { ButtonComponent } from '../../../../../@core/components/ui/button/button.component';
import { CONTRACT_USER_ROLE } from '../../../../../@core/models/model';
import { CONTRACT_STATUS } from '../../../../../@shared/components/badge/badge.enums';
import { SIGNATORY_TYPE, IContractSignatoryConf } from '../../../../models/contract-management.model';
import { EditContractDataService } from '../../../../services/edit-contract-data.service';
import { AssetUrlPipe } from '../../../../../@shared/pipes/asset-url.pipe';
import { SocketService, SOCKET_EVENTS } from '../../../../../@core/services/socket.service';
import { Subscription } from 'rxjs';

@Component({
    selector: 'app-signatories-tab',
    standalone: true,
    imports: [CommonModule, ApprovalWorkflowComponent, ButtonComponent, ButtonModule, TooltipModule, AssetUrlPipe],
    template: `
        <div class="h-full w-full overflow-auto">
        <div *ngIf="signatoryConf() && signatoryConf()!.signatories.length === 0; else signatoriesList"
            class="inn-flex-container flex flex-col justify-center h-full">
            <div class="flex flex-col justify-center items-center gap-2 p-2" style="margin-top: 10%">
                <div class="signatories-file">
                    <img [src]="'images/signed_doc.svg' | assetUrl" alt="link" class="relative z-0">
                    <div class="vector-signatories-left">
                        <img [src]="'images/orange-top.svg' | assetUrl" alt="">
                    </div>
                    <div class="vector-signatories-right">
                        <img [src]="'images/blue-bottom.svg' | assetUrl" alt="">
                    </div>
                </div>
                <div class="flex flex-col items-center justify-center text-center pe-2">
                    <h2 class="font-semibold text-base mb-2">No Signatories Added Yet</h2>
                    <p class="text-sm">Signatures will be shown here once people start signing the document</p>
                </div>
                <div class="flex flex-col items-center justify-center gap-2 w-full relative z-20">
                    @if(currentRole() === CONTRACT_USER_ROLE.OWNER || currentRole() === CONTRACT_USER_ROLE.EDITOR){
                    <app-button
                        label="Add Signatories"
                        icon="pi pi-plus"
                        severity="primary"
                        [disabled]="disableAddSignatories()"
                        (clicked)="toggleSignatoryDialog()" />
                    }
                </div>
            </div>
        </div>

        <ng-template #signatoriesList>
            <div class="flex flex-col p-2 h-full">
                <div class="flex flex-row justify-between">
                    <div class="flex items-center">
                        <span class="text-lg font-semibold">Signatories</span>
                        <div class="flex ml-2">
                            @if(signatoryType() == SIGNATORY_TYPE.ADOBE_SIGN){
                            <div class="flex items-center" [pTooltip]="tooltipContentAdobe" tooltipPosition="top">
                                <img [src]="'images/logos/adobe.png' | assetUrl" height="24" width="24" alt="icon">
                            </div>
                            }@else if(signatoryType() == SIGNATORY_TYPE.DOCU_SIGN){
                            <div class="flex items-center" [pTooltip]="tooltipContentDocu" tooltipPosition="top">
                                <img [src]="'images/logos/docusign.png' | assetUrl" height="24" width="24" alt="icon">
                            </div>
                            }
                        </div>
                    </div>
                    <div *ngIf="permissions()?.canEdit">
                        <app-button variant="outlined" severity="contrast" label="Reset" icon="pi pi-refresh"
                            [disabled]="contract()?.status != CONTRACT_STATUS.SIGNATURE_IN_PROGRESS"
                            (clicked)="resetSignatoryFlow()" />
                    </div>
                </div>
                <div class="flex-grow overflow-hidden mt-4">
                    <div class="h-full overflow-auto no-scrollbar">
                        <app-approval-workflow
                            *ngIf="signatoryConf() && signatoryConf()!.signatories.length > 0"
                            [signatories]="allSignatories()" [flowType]="flowType.SIGNATORY"
                            (deleteSignatoryEvent)="signatoryChange($event)"
                            (downloadSignatoryEvent)="downloadSignedDocument($event)"
                            [contractStatus]="contract()?.status || CONTRACT_STATUS.DRAFT">
                        </app-approval-workflow>
                    </div>
                </div>
                <div *ngIf="permissions()?.canEdit" class="flex flex-row justify-center p-2">
                    <app-button
                        [label]="(signatoryConf()?.signatories?.length || 0) === 0 ? 'Add Signatory' : 'Edit Signatories'"
                        [icon]="(signatoryConf()?.signatories?.length || 0) === 0 ? 'pi pi-plus' : 'pi pi-pencil'"
                        [disabled]="disableAddSignatories()" variant="outlined" severity="contrast"
                        (clicked)="toggleSignatoryDialog()" />
                </div>
            </div>
        </ng-template>
        </div>
    `,
    styles: [`
        :host {
            display: block;
            height: 100%;
            width: 100%;
        }
        .signatories-file {
            position: relative;
            display: inline-block;
        }
        .vector-signatories-left {
            position: absolute;
            top: -8rem;
            left: -10rem;
            right: -4rem;
            pointer-events: none;
            z-index: -1;
        }
        .vector-signatories-right {
            position: absolute;
            bottom: -8rem;
            right: -10rem;
            left: -4rem;
            pointer-events: none;
            z-index: -1;
        }
    `]
})
export class SignatoriesTabComponent implements OnInit, OnDestroy {
    tooltipContentAdobe = 'Adobe Sign';
    tooltipContentDocu = 'DocuSign';
    CONTRACT_USER_ROLE = CONTRACT_USER_ROLE;
    CONTRACT_STATUS = CONTRACT_STATUS;
    SIGNATORY_TYPE = SIGNATORY_TYPE;
    flowType = FLOW_TYPE;

    private signatoriesUpdatedSubscription?: Subscription;

    constructor(
        public dataService: EditContractDataService,
        private socketService: SocketService
    ) { }

    // Use getter methods to access computed signals from the service - these will auto-update!
    get signatoryConf() { return this.dataService.signatoryConf; }
    get allSignatories() { return this.dataService.allSignatories; }
    get currentRole() { return this.dataService.currentRole; }
    get permissions() { return this.dataService.permissions; }
    get contract() { return this.dataService.contract; }
    get disableAddSignatories() { return this.dataService.disableAddSignatories; }
    get signatoryType() { return this.dataService.signatoryType; }

    ngOnInit() {
        this.setupSocketListener();
    }

    ngOnDestroy() {
        if (this.signatoriesUpdatedSubscription) {
            this.signatoriesUpdatedSubscription.unsubscribe();
        }
    }

    setupSocketListener() {
        this.signatoriesUpdatedSubscription = this.socketService
            .on<{ contractId: string; signatoryConf: IContractSignatoryConf }>(SOCKET_EVENTS.SIGNATORIES_UPDATED)
            .subscribe(res => {
                const data = res[0];
                if (data && data.contractId === this.dataService.contractId()) {
                    // Update the signatory configuration in the data service
                    const allSignatories = JSON.parse(JSON.stringify(data.signatoryConf.signatories || []));
                    this.dataService.updateData({
                        signatoryConf: data.signatoryConf,
                        allSignatories: allSignatories
                    });
                }
            });
    }

    toggleSignatoryDialog() {
        const data = this.dataService.getData();
        if (data && data.parent) data.parent.toggleSignatoryDialog();
    }

    resetSignatoryFlow() {
        const data = this.dataService.getData();
        if (data && data.parent) data.parent.resetSignatoryFlow();
    }

    signatoryChange(email: string) {
        const data = this.dataService.getData();
        if (data && data.parent) data.parent.signatoryChange(email);
    }

    downloadSignedDocument(event: any) {
        const data = this.dataService.getData();
        if (data && data.parent) data.parent.downloadSignedDocument(event);
    }
}

